import Ionicons from "@expo/vector-icons/Ionicons";
import { useRouter } from "expo-router";
import { useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";

import { ThemedText } from "@/components/themed-text";
import { Button } from "@/components/ui/button";
import { Screen } from "@/components/ui/screen";
import { MODES } from "@/constants/options";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import { onboardingStrings as strings } from "@/i18n/features/onboarding.i18n";
import { notify } from "@/lib/dialog";
import { useAuthStore } from "@/state/auth-store";
import type { Mode } from "@/types";

export default function ModeSelection() {
  const router = useRouter();
  const { t } = useTranslation();
  const c = useTheme();
  const setMode = useAuthStore((s) => s.setMode);
  const skipMode = useAuthStore((s) => s.skipMode);

  const [selected, setSelected] = useState<Mode | null>(null);
  const [busy, setBusy] = useState(false);

  async function onContinue() {
    setBusy(true);
    try {
      if (selected) await setMode(selected);
      else await skipMode();
      router.replace("/(tabs)/home");
    } catch {
      // Same missing-catch defect as the permissions screen's finish():
      // `setMode` writes to the API, and without this the rejection escaped
      // the handler — spinner stops, nothing happens, no way to know why.
      notify(t("errors.generic"), t("errors.tryAgainInAMoment"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen
      scroll
      footer={
        <View style={styles.actions}>
          <Button
            label={selected ? t(strings.continue) : t(strings.skipForNow)}
            loading={busy}
            onPress={onContinue}
          />
        </View>
      }
    >
      <View style={styles.header}>
        <ThemedText variant="title">{t(strings.modeTitle)}</ThemedText>
        <ThemedText variant="body" tone="secondary">
          {t(strings.modeDescription)}
        </ThemedText>
      </View>

      <View style={styles.list}>
        {MODES.map((m) => {
          const active = selected === m.id;
          return (
            <Pressable
              key={m.id}
              onPress={() => setSelected(active ? null : m.id)}
              style={[
                styles.option,
                {
                  backgroundColor: active ? c.backgroundSelected : c.surface,
                  borderColor: active ? c.tint : c.border,
                },
              ]}
            >
              <ThemedText style={styles.emoji}>{m.emoji}</ThemedText>
              <View style={styles.optionText}>
                <ThemedText variant="bodyStrong">{m.title}</ThemedText>
                <ThemedText variant="caption" tone="secondary">
                  {m.subtitle}
                </ThemedText>
              </View>
              <Ionicons
                name={active ? "radio-button-on" : "radio-button-off"}
                size={22}
                color={active ? c.tint : c.textMuted}
              />
            </Pressable>
          );
        })}
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  header: { gap: Spacing.sm, paddingTop: Spacing.sm, marginBottom: Spacing.xl },
  list: { gap: Spacing.md },
  option: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.md,
    padding: Spacing.lg,
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  emoji: { fontSize: 26 },
  optionText: { flex: 1, gap: 2 },
  actions: { gap: Spacing.sm },
});
